import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  Injector,
  Input,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { CRMIntegrationsService } from '../../../services/crm-integrations.service';
import { publicMethods } from '../../../globals';
import { forkJoin } from 'rxjs';
import { concatMap, filter, tap } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MessageService } from '../../../services/message.service';
import { ZintAutocompleteComponent } from '../../../app/html-snippet-components/zint-autocomplete/zint-autocomplete.component';
import { ZintModalConfig, ZintModalContent } from '../../../classes/types';
declare var $: any;

@Component({
    selector: 'wizard-crm-lookup-field',
    templateUrl: './wizard-crm-lookup-field.component.html',
    styleUrl: './wizard-crm-lookup-field.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class WizardCrmLookupFieldComponent {
  @Input() stepData: Record<string, any>;
  @ViewChildren(ZintAutocompleteComponent)
  autocompletes!: QueryList<ZintAutocompleteComponent>;

  crmService = inject(CRMIntegrationsService);
  pubMethods = inject(publicMethods);
  messageService = inject(MessageService);
  cdr = inject(ChangeDetectorRef);
  snackbar = inject(MatSnackBar);
  injector = inject(Injector);

  CRM_OBJECT_TYPE = 'crm_object_type';
  LIST_OBJECT_FIELDS = 'list_crm_object_fields';
  CRM_OBJECT_FIELDS = 'crm_object_fields';
  LIST_TRANSFORM_OPTIONS = 'list_all_transformers';
  GET_OR_UPDATE_TRANSFORMER = 'transform';

  crmType: string = '';
  stepType: string = '';
  existingLookupFieldState: Record<string, any> = null;
  crmObjectID = computed(() => {
    return this.crmService.configCRMSelectedObjectId();
  });
  lookupFieldID: number = null;
  existingLookupField: Record<string, any>;
  selectedDomainLookup: string = '';

  crmLookupFields: Record<string, any>[] = [];
  allLookupTransformOptions: Record<string, any>[] = [];
  existingTransformOptions: Record<string, any>[] = [];
  selectedTransformer: any;

  showAdvancedOptionsModal = false;
  advancedHelpModalConfig: ZintModalConfig;
  advancedHelpModalContent: ZintModalContent = {};

  ngOnInit() {
    this.crmType = this.crmService.configCRMType();
    this.stepType = this.stepData.stepType;
    this.getStepActionContent();
  }

  getStepActionContent(): void {
    if (!this.crmObjectID()) {
      this.crmService
        .getCRMExistingState({ actionType: this.CRM_OBJECT_TYPE })
        .subscribe(data => {
          if (data?.length) {
            this.crmService.configCRMSelectedObjectId.update(() => data[0].id);

            this.listFieldsAndGetExistingSelection();
          }
        });
    } else if (this.crmObjectID()) {
      this.listFieldsAndGetExistingSelection();
    }
  }

  listFieldsAndGetExistingSelection(): void {
    forkJoin({
      stepActionContent: this.crmService.getCRMStepActionContent({
        stepType: this.stepType,
        actionType: this.LIST_OBJECT_FIELDS,
        qParam: '?zint_field_type=Text',
        objectId: this.crmObjectID(),
        refetch: true,
      }),
      getAllTransformers: this.crmService.getCRMStepActionContent({
        stepType: this.stepType,
        actionType: this.LIST_TRANSFORM_OPTIONS,
        objectId: this.crmObjectID(),
      }),
      stepState: this.crmService.getCRMExistingState({
        actionType: this.CRM_OBJECT_FIELDS,
        objectId: this.crmObjectID(),
        fieldId: null,
      }),
      existingTransformers: this.crmService.getCRMExistingState({
        actionType: this.GET_OR_UPDATE_TRANSFORMER,
        objectId: this.crmObjectID(),
        fieldId: null,
      }),
    })
      .pipe(
        tap(results => {
          this.crmLookupFields = results.stepActionContent;
          this.existingLookupFieldState = results.stepState;
          this.allLookupTransformOptions = results.getAllTransformers;
          this.existingTransformOptions = results.existingTransformers;
          this.handleExistingLookupStepData();
        })
      )
      .subscribe();
  }

  handleExistingLookupStepData(): void {
    if (!this.pubMethods.hasData(this.existingLookupFieldState)) return;

    const foundExisting = this.existingLookupFieldState.find(
      result => result.lookupField === 1
    );

    if (foundExisting) {
      this.existingLookupField = foundExisting;
      this.lookupFieldID = foundExisting.id;
    }

    // handle existing transformers for the toggle ui
    if (this.pubMethods.hasData(this.existingTransformOptions)) {
      this.existingTransformOptions.forEach(existingOption => {
        this.allLookupTransformOptions.forEach(option => {
          if (existingOption.transform === option.value) {
            option.checked = true;
            option.transformId = existingOption.id;
          }
        });
      });
    }
  }

  postChoiceAndGetStatus(selectedField: Record<string, any>): void {
    const { name, label, fieldType } = selectedField;

    // Update if existing lookupField
    if (this.lookupFieldID) {
      if (name === this.existingLookupField.name) return;

      const reqBody = { name, label, fieldType };
      this.crmService
        .updateCRMStepActionContent({
          updateActionType: this.CRM_OBJECT_FIELDS,
          params: reqBody,
          objectId: this.crmObjectID(),
          fieldId: this.lookupFieldID,
          updateMethod: 'put',
        })
        .pipe(
          tap(response => {
            if (response?.ok) {
              // update lookup field id
              this.lookupFieldID = response.body?.id;

              // ready check
              this.crmService.getCRMConfigStepStatus(this.stepType);
            } else {
              const errorDetails = response.error?.error || response.body;
              const errMsg = Object.values(errorDetails).flat().join('. ');
              if (errMsg) {
                this.messageService.show(errMsg);
              } else {
                console.warn('something went wrong', response);
              }
            }
          }),
          filter(response => response.ok),
          concatMap(() => {
            // delete the previous lookup field
            return this.crmService.updateCRMStepActionContent({
              updateActionType: this.CRM_OBJECT_FIELDS,
              objectId: this.crmObjectID(),
              fieldId: this.existingLookupField.id,
              updateMethod: 'delete',
            });
          })
        )
        .subscribe();
      return;
    }

    // Create if new
    const reqBody = { name, label, fieldType, lookupField: 1 };

    this.crmService
      .postCRMStepActionJSON({
        postActionType: this.CRM_OBJECT_FIELDS,
        params: reqBody,
        objectId: this.crmObjectID(),
      })
      .subscribe(response => {
        if (this.pubMethods.hasData(response.body)) {
          this.lookupFieldID = response.body.id;

          //step readiness
          this.crmService.getCRMConfigStepStatus(this.stepType);
        }
      });
  }

  setTransformOption(val: Record<string, any>, checked?: boolean) {
    // DELETE if false
    if (checked === false) {
      this.crmService
        .updateCRMStepActionContent({
          updateActionType: this.GET_OR_UPDATE_TRANSFORMER,
          objectId: this.crmObjectID(),
          fieldId: val.transformId,
          updateMethod: 'delete',
        })
        .subscribe(response => {
          if (response.ok) {
            this.snackbar.open('Option removed', 'OK', {
              duration: 2200,
            });
          } else {
            console.warn('delete went wrong', response);
          }
        });
    }

    // POST if true
    if (checked === true) {
      const reqBody = { crmField: this.lookupFieldID, transform: val.value };
      this.crmService
        .postCRMStepActionJSON({
          postActionType: this.GET_OR_UPDATE_TRANSFORMER,
          params: reqBody,
          objectId: this.crmObjectID(),
        })
        .subscribe(response => {
          if (response.ok) {
            this.updateTransformOptionObject(response.body);
            this.snackbar.open('Advanced option saved', 'OK', {
              duration: 2200,
            });
          } else {
            console.warn('something went wrong', response);
          }
        });
    }
  }

  updateTransformOptionObject(updatedObj: Record<string, any>): void {
    const foundObjIndex = this.allLookupTransformOptions.findIndex(
      option => option.value === updatedObj.transform
    );
    this.allLookupTransformOptions[foundObjIndex].transformId = updatedObj.id;
  }

  toggleAdvancedOptionModal(): void {
    this.advancedHelpModalContent = {
      htmlElement: `        
          <img src='https://zint-static.s3.eu-west-2.amazonaws.com/crm-visuals/PaddingActivatedSmall.jpg' alt='img' class="w-100" /><br>
          <a href="https://support.zint.io/" target="_blank">Zendesk Guide</a>   
        ` as any as HTMLElement,
    };
    this.advancedHelpModalConfig = { vCentered: true, name: 'lookupHelp' };
    this.showAdvancedOptionsModal = true;
    this.cdr.detectChanges();
    this.pubMethods.triggerZintModal(this.advancedHelpModalConfig.name);
  }
}
